import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  children?: React.ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Custom in-page confirmation modal.
 *
 * IMPORTANT: never replace this with window.confirm()/alert()/prompt() anywhere
 * in the Super Admin Panel. Those are silently blocked inside this app's
 * sandboxed live-preview iframe (they return `false` immediately with no
 * dialog ever shown) — a real bug found and fixed while building Page 5/6.
 * This component is the one safe way to ask for confirmation.
 */
export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-150"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 border dark:border-slate-800 shadow-xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          {destructive && (
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 shrink-0">
              <AlertTriangle size={20} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold dark:text-white">{title}</h3>
            {description && (
              <div className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</div>
            )}
          </div>
        </div>

        {children && <div className="pt-1">{children}</div>}

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={loading} className="dark:border-slate-700 dark:text-white">
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            className={destructive ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-primary hover:bg-primary/90 text-white'}
          >
            {loading ? 'Please wait...' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal;
